/* Step 01 trade deck, ported from the prototype (iq-top1-files-lp, "step 01: pilha de gráficos"): each card is one
   trader's trade (win, loss or blown account), seeded so every render draws the same charts. Drawing is in design px;
   the caller passes `k` (root rem px / 8) so the canvas scales with the page. */

export type TradeKind = "w" | "l" | "b";
type Candle = { o: number; c: number; hi: number; lo: number };
export type Trade = {
  t: TradeKind; cs: Candle[]; ma: number[]; TA: number; TB: number; lim: number | null; hit: number;
  mn: number; mx: number; sym: string; pnl: number;
};

export const DECK_N = 36;
const SY = ["NQ · 5M", "ES · 1M", "CL · 15M", "GC · 5M", "YM · 5M", "RTY · 1M", "NQ · 1M", "ES · 5M"];
const TY: TradeKind[] = ["w", "l", "w", "b", "w", "l", "w", "b"];
const PN = [960, 410, 1240, 0, 780, 325, 1580, 0];
export const TAG: Record<TradeKind, string> = { w: "Win", l: "Loss", b: "Blown account" };

export function makeTrade(n: number): Trade {
  const N = DECK_N, t = TY[n % TY.length];
  let sd = 97 + n * 131;
  const R = () => { sd = (sd * 16807) % 2147483647; return sd / 2147483647; };
  let v = 100;
  const cs: Candle[] = [];
  for (let i = 0; i < N; i++) {
    const o = v;
    v += t === "w" ? (R() - 0.46) * 8 + (i > 16 ? 1.1 : -0.5) : t === "l" ? (R() - 0.52) * 8 + (i > 18 ? -1.2 : 0.7) : (R() - 0.55) * 8 - 0.75;
    cs.push({ o, c: v, hi: Math.max(o, v) + R() * 3.5, lo: Math.min(o, v) - R() * 3.5 });
  }
  let TA = 14, TB = 24;
  if (t === "w") { TA = cs.slice(8, 18).reduce((b, c, i) => (c.c < cs[b].c ? i + 8 : b), 8); TB = Math.min(N - 4, TA + 10); }
  if (t === "l") { TA = cs.slice(12, 22).reduce((b, c, i) => (c.c > cs[b].c ? i + 12 : b), 12); TB = Math.min(N - 4, TA + 9); }
  let lim: number | null = null, hit = N;
  if (t === "b") {
    const l = cs[28].c + 2;
    lim = l;
    hit = cs.findIndex((c, i) => i > 8 && c.lo <= l);
    if (hit < 0) hit = N - 1;
  }
  const all = cs.flatMap((c) => [c.hi, c.lo]);
  let mn = Math.min(...all);
  const mx = Math.max(...all);
  if (lim !== null) mn = Math.min(mn, lim - 3);
  const ma = cs.map((_, i) => { const a = cs.slice(Math.max(0, i - 5), i + 1); return a.reduce((s, x) => s + x.c, 0) / a.length; });
  return { t, cs, ma, TA, TB, lim, hit, mn, mx, sym: SY[n % SY.length], pnl: PN[n % PN.length] };
}

const mint = (a: number) => `rgba(158,227,176,${a})`;
const red = (a: number) => `rgba(255,107,94,${a})`;

/** `si`: how many candles the scanner has read; `live`: animate the candles growing and draw the scan line. */
export function drawTrade(cv: HTMLCanvasElement, S: Trade, si: number, now: number, live: boolean, k: number, font: string) {
  const g = cv.getContext("2d");
  const N = DECK_N, dpr = Math.min(2, devicePixelRatio || 1), W = cv.clientWidth / k, H = cv.clientHeight / k;
  if (!g || !W || !H) return;
  const pw = Math.round(cv.clientWidth * dpr), ph = Math.round(cv.clientHeight * dpr);
  if (cv.width !== pw || cv.height !== ph) { cv.width = pw; cv.height = ph; }
  g.setTransform(dpr * k, 0, 0, dpr * k, 0, 0);
  g.clearRect(0, 0, W, H);
  const T0 = 24, CH = H - 44, cw = W / N, X = (i: number) => i * cw + cw / 2, Y = (p: number) => T0 + CH - ((p - S.mn) / (S.mx - S.mn)) * CH;
  const stop = S.t === "b" ? S.hit + 1 : N, lim = Math.min(si, stop), scan = Math.min(W - 1, lim * cw);
  const label = `500 14px ${font}`;

  g.fillStyle = "rgba(255,255,255,.04)";
  for (let j = 0; j <= 4; j++) g.fillRect(0, Math.round(T0 + (j * CH) / 4), W, 1);
  g.fillStyle = "rgba(255,255,255,.03)";
  for (let j = 1; j < 6; j++) g.fillRect(Math.round((j * W) / 6), 0, 1, H);
  if (S.lim !== null) {
    const yl = Math.round(Y(S.lim));
    g.fillStyle = red(0.55);
    for (let x = 0; x < W; x += 6) g.fillRect(x, yl, 3, 1);
    g.font = label;
    g.fillStyle = red(0.8);
    g.fillText("Max drawdown", 8, yl + 18);
  }

  S.cs.forEach((q, i) => {
    if (i >= stop) return;
    const x = Math.round(X(i)), bw = Math.max(2, Math.round(cw * 0.56)), kk = live ? Math.max(0, Math.min(1, (si - i) * 2.2)) : 1, up = q.c >= q.o;
    if (kk <= 0) {
      g.fillStyle = "rgba(255,255,255,.08)";
      g.fillRect(x, Y(q.hi), 1, Y(q.lo) - Y(q.hi));
      g.fillRect(Math.round(x - bw / 2), Math.min(Y(q.o), Y(q.c)), bw, Math.max(1, Math.abs(Y(q.o) - Y(q.c))));
      return;
    }
    const e = 1 - Math.pow(1 - kk, 3), cc = q.o + (q.c - q.o) * e, hi = q.o + (q.hi - q.o) * e, lo = q.o + (q.lo - q.o) * e;
    g.fillStyle = up ? mint(0.35 + 0.6 * e) : `rgba(255,255,255,${0.2 + 0.3 * e})`;
    g.fillRect(x, Y(Math.max(hi, cc)), 1, Y(Math.min(lo, cc)) - Y(Math.max(hi, cc)));
    g.fillRect(Math.round(x - bw / 2), Math.min(Y(q.o), Y(cc)), bw, Math.max(1, Math.abs(Y(q.o) - Y(cc))));
  });

  const li = Math.max(0, Math.min(stop - 1, Math.floor(lim)));
  const pts: [number, number][] = [];
  for (let i = 0; i <= li; i++) pts.push([X(i), Y(S.ma[i])]);
  const col = S.t === "w" ? mint : red;
  if (pts.length > 1) {
    g.beginPath();
    g.moveTo(pts[0][0], H);
    pts.forEach((p) => g.lineTo(p[0], p[1]));
    g.lineTo(pts[pts.length - 1][0], H);
    g.closePath();
    const gr = g.createLinearGradient(0, T0, 0, H);
    gr.addColorStop(0, col(0.12));
    gr.addColorStop(1, col(0));
    g.fillStyle = gr;
    g.fill();
    g.beginPath();
    pts.forEach((p, j) => (j ? g.lineTo(p[0], p[1]) : g.moveTo(p[0], p[1])));
    g.strokeStyle = col(0.7);
    g.lineWidth = 1.4;
    g.lineJoin = "round";
    g.stroke();
  }

  // entry / exit marker: a square that pops in with a little overshoot
  const dot = (x: number, y: number, kk: number) => {
    const s = kk <= 0 ? 0 : kk >= 1 ? 1 : 1 - Math.cos(kk * Math.PI * 2.2) * Math.exp(-5.5 * kk);
    if (s <= 0) return;
    g.save();
    g.translate(x, y);
    g.scale(s, s);
    g.fillStyle = "#151816";
    g.fillRect(-6, -6, 12, 12);
    g.strokeStyle = col(0.95);
    g.lineWidth = 1;
    g.strokeRect(-5.5, -5.5, 11, 11);
    g.fillStyle = col(1);
    g.fillRect(-2, -2, 4, 4);
    g.restore();
  };
  if (S.t !== "b") {
    const xa = X(S.TA), ya = Y(S.cs[S.TA].c), xb = X(S.TB), yb = Y(S.cs[S.TB].c), kA = Math.max(0, Math.min(1, (lim - S.TA - 0.3) * 1.6));
    if (kA > 0) {
      const e = Math.min(1, (lim - S.TA) / (S.TB - S.TA));
      g.setLineDash([3, 3]);
      g.lineDashOffset = -now / 60;
      g.strokeStyle = col(0.85);
      g.lineWidth = 1;
      g.beginPath();
      g.moveTo(xa, ya);
      g.lineTo(xa + (xb - xa) * e, ya + (yb - ya) * e);
      g.stroke();
      g.setLineDash([]);
      dot(xa, ya, kA);
    }
    const kB = Math.max(0, Math.min(1, (lim - S.TB - 0.3) * 1.6));
    if (kB > 0) {
      dot(xb, yb, kB);
      const e = 1 - Math.pow(1 - kB, 3), sign = S.t === "w" ? "+$" : "−$", tx = sign + Math.round(S.pnl * e).toLocaleString("en-US");
      g.font = label;
      const tw = g.measureText(sign + S.pnl.toLocaleString("en-US")).width + 16;
      const bx = Math.max(4, Math.min(W - tw - 4, (xa + xb) / 2 - tw / 2)), by = Math.max(4, Math.min(ya, yb) - 40 + 6 * (1 - e));
      g.globalAlpha = e;
      g.fillStyle = col(0.14);
      g.beginPath();
      g.rect(bx, by, tw, 24);
      g.fill();
      g.strokeStyle = col(0.45);
      g.stroke();
      g.fillStyle = col(1);
      g.textAlign = "center";
      g.fillText(tx, bx + tw / 2, by + 17);
      g.textAlign = "left";
      g.globalAlpha = 1;
    }
  } else if (S.lim !== null) {
    const kH = Math.max(0, Math.min(1, (si - S.hit - 0.6) * 1.4));
    if (kH > 0) {
      const x = X(S.hit), y = Y(S.lim);
      g.strokeStyle = red(0.9 * kH);
      g.lineWidth = 1.5;
      g.beginPath();
      g.moveTo(x - 5, y - 5);
      g.lineTo(x + 5, y + 5);
      g.moveTo(x + 5, y - 5);
      g.lineTo(x - 5, y + 5);
      g.stroke();
      if (kH < 1) {
        g.strokeStyle = red(0.5 * (1 - kH));
        g.lineWidth = 1;
        g.beginPath();
        g.arc(x, y, 8 + kH * 18, 0, 6.283);
        g.stroke();
      }
    }
  }

  if (live && si < stop + 0.5) {
    const yc = Y(S.cs[Math.min(stop - 1, Math.floor(lim))].c), sg = g.createLinearGradient(scan - 70, 0, scan, 0);
    sg.addColorStop(0, col(0));
    sg.addColorStop(1, col(0.1));
    g.fillStyle = sg;
    g.fillRect(scan - 70, 0, 70, H);
    g.fillStyle = col(0.9);
    g.fillRect(Math.round(scan), 0, 1, H);
    g.fillStyle = col(0.2);
    for (let x = 0; x < W; x += 6) g.fillRect(x, Math.round(yc), 3, 1);
  }
}
